import dayjs from 'dayjs';

import {
  Box,
  Stack,
  Dialog,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
} from '@mui/material';

import { fDate } from 'src/utils/format-time';
import { getTime } from 'src/utils/constants';

import {
  ALL_DAY_ACTIVITY_TIME,
  PLACEHOLDER_ACTIVITY_DATE_ISO,
  PLACEHOLDER_ACTIVITY_START_TIME,
} from 'src/constants/activity';

import { Iconify } from './iconify';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: () => void;
  activities: any[];
  displayType: 'locations' | 'dates' | 'times';
  /** Tarihi geçmiş aktivitelerin detay sayfasında geçmiş tarihleri de göster */
  includePast?: boolean;
};

export function ActivityDetailsDialog({
  open,
  onClose,
  activities,
  displayType,
  includePast = false,
}: Props) {
  const getTitle = () => {
    switch (displayType) {
      case 'locations':
        // Tekli lokasyon varsa İlçe / İl formatında göster
        if (activities && activities.length > 0) {
          const locationGroups = new Map<string, any[]>();
          activities.forEach((activity: any) => {
            const locationId = activity?.location?._id || 'no-location';
            if (!locationGroups.has(locationId)) {
              locationGroups.set(locationId, []);
            }
            locationGroups.get(locationId)!.push(activity);
          });

          // Tek lokasyon varsa başlığı dinamik yap
          if (locationGroups.size === 1) {
            const firstActivity = activities[0];
            const location = firstActivity?.location;
            const district = location?.district;
            const city = location?.city;

            if (district || city) {
              if (district === city) {
                return city || 'Konumlar';
              }
              return district && city ? `${district} / ${city}` : district || city || 'Konumlar';
            }
          }
        }
        return 'Konumlar';
      case 'dates':
        return 'Tarihler';
      case 'times':
        return 'Saatler';
      default:
        return 'Detaylar';
    }
  };

  const renderContent = () => {
    if (!activities || activities.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Bilgi bulunamadı
        </Typography>
      );
    }

    // Bugünden önceki tarihleri filtrele (dün ve daha eski) - eski tarihli kartları tamamen gizle
    const today = dayjs().startOf('day');
    const filteredActivities = activities.filter((activity: any) => {
      const schedule = activity?.schedule;
      // Tarih bilgisi yoksa veya placeholder ise göster
      if (
        includePast ||
        !schedule?.startDate ||
        schedule.startDate === PLACEHOLDER_ACTIVITY_DATE_ISO
      ) {
        return true;
      }
      // Geçerli tarih varsa, sadece bugün ve gelecek tarihleri göster (dün ve daha eski tarihleri gizle)
      const activityDate = dayjs(schedule.startDate).startOf('day');
      return activityDate.isAfter(today) || activityDate.isSame(today, 'day');
    });

    if (filteredActivities.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Gelecek tarih bulunamadı
        </Typography>
      );
    }

    // Lokasyonlara göre grupla
    const locationGroups = new Map<string, any[]>();

    filteredActivities.forEach((activity: any) => {
      const locationId = activity?.location?._id || 'no-location';
      if (!locationGroups.has(locationId)) {
        locationGroups.set(locationId, []);
      }
      locationGroups.get(locationId)!.push(activity);
    });

    // Her lokasyon grubu için render et
    return (
      <Stack spacing={3}>
        {Array.from(locationGroups.entries()).map(([locationId, locationActivities]) => {
          const firstActivity = locationActivities[0];
          const location = firstActivity?.location;

          // İlçe ve il bilgisini oluştur
          const getDistrictCity = () => {
            const district = location?.district;
            const city = location?.city;

            if (district || city) {
              if (district === city) {
                return city || null;
              }
              return district && city ? `${district}/${city}` : district || city || null;
            }

            return null;
          };

          const locationName = location?.address?.title || null;
          const districtCity = getDistrictCity();
          const locationAddress = location?.address?.description || '';

          // Tüm günleri topla ve sırala
          const allDays: any[] = [];
          locationActivities.forEach((activity: any) => {
            const schedule = activity?.schedule;
            if (schedule?.days && Array.isArray(schedule.days)) {
              schedule.days.forEach((day: any) => {
                // Placeholder tarihleri de dahil et, geçmiş tarihleri filtrele
                const isPlaceholderDate = !day.date || day.date === PLACEHOLDER_ACTIVITY_DATE_ISO;
                if (isPlaceholderDate) {
                  allDays.push({
                    ...day,
                    activityId: activity._id,
                  });
                } else {
                  const dayDate = dayjs(day.date).startOf('day');
                  if (includePast || dayDate.isAfter(today) || dayDate.isSame(today, 'day')) {
                    allDays.push({
                      ...day,
                      activityId: activity._id,
                    });
                  }
                }
              });
            }
          });

          // Tarihe göre sırala (placeholder tarihleri en sona)
          allDays.sort((a, b) => {
            const isPlaceholderA = !a.date || a.date === PLACEHOLDER_ACTIVITY_DATE_ISO;
            const isPlaceholderB = !b.date || b.date === PLACEHOLDER_ACTIVITY_DATE_ISO;

            // Placeholder tarihleri en sona
            if (isPlaceholderA && !isPlaceholderB) return 1;
            if (!isPlaceholderA && isPlaceholderB) return -1;
            if (isPlaceholderA && isPlaceholderB) return 0;

            const dateA = dayjs(a.date).valueOf();
            const dateB = dayjs(b.date).valueOf();
            return dateA - dateB;
          });

          if (allDays.length === 0) {
            return null;
          }

          return (
            <Box key={locationId}>
              {/* Lokasyon bilgisi - yeşil blok */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: '#E8F5E9',
                  mb: 1,
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Iconify icon="mdi:map-marker" width={24} sx={{ color: '#D32F2F', mt: 0.5 }} />
                  <Stack spacing={0.5} sx={{ flex: 1 }}>
                    {/* Konum adı - kalın başlık */}
                    {locationName && (
                      <Typography variant="body1" fontWeight={600} color="text.primary">
                        {locationName}
                      </Typography>
                    )}
                    {/* İlçe/İl bilgisi */}
                    {districtCity && (
                      <Typography variant="body2" color="text.secondary">
                        {districtCity}
                      </Typography>
                    )}
                    {/* Adres açıklaması */}
                    {locationAddress && (
                      <Typography variant="caption" color="text.secondary">
                        {locationAddress}
                      </Typography>
                    )}
                    {/* Eğer hiçbir bilgi yoksa */}
                    {!locationName && !districtCity && !locationAddress && (
                      <Typography variant="body2" color="text.secondary">
                        Belirtilmemiş
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Box>

              {/* Gösteriler listesi - mavi çizgi ile bağlantılı */}
              <Stack
                spacing={1}
                sx={{ pl: 3, borderLeft: '2px solid', borderColor: 'primary.light' }}
              >
                {allDays.map((day: any, dayIndex: number) => {
                  const hasValidDate = day.date && day.date !== PLACEHOLDER_ACTIVITY_DATE_ISO;
                  const dateText = hasValidDate
                    ? fDate(day.date, 'DD.MM.YYYY (dddd)')
                    : 'Tarih Sor';

                  const hasValidStartTime =
                    typeof day.startTime === 'number' &&
                    day.startTime !== PLACEHOLDER_ACTIVITY_START_TIME;
                  const isAllDay = day.startTime === ALL_DAY_ACTIVITY_TIME;
                  const isPlaceholderTime =
                    typeof day.startTime === 'number' &&
                    day.startTime === PLACEHOLDER_ACTIVITY_START_TIME;

                  let timeText = 'Saat Sor';
                  if (isAllDay) {
                    timeText = '24 saat açık';
                  } else if (hasValidStartTime) {
                    const mappedTime = getTime(day.startTime);
                    timeText = mappedTime ?? 'Saat Sor';
                  } else if (isPlaceholderTime) {
                    timeText = 'Saat Sor';
                  }

                  return (
                    <Stack
                      key={`${day._id || dayIndex}-${day.date}`}
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ py: 0.5 }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ minWidth: 200 }}
                      >
                        <Iconify
                          icon="solar:calendar-linear"
                          width={20}
                          sx={{ color: 'text.secondary' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {dateText}
                        </Typography>
                      </Stack>
                      {timeText && (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Iconify
                            icon="solar:clock-circle-linear"
                            width={20}
                            sx={{ color: 'text.secondary' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {timeText}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontFamily="Rubik" fontWeight={600}>
          {getTitle()}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Iconify icon="mdi:close" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '60vh' }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
